import React, { useState, useEffect, useCallback } from "react";
import { Grid, Card, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";
import { useNavigate } from "react-router-dom";

import PartnerTable from "./components/PartnerTable";
import PaginationControls from "../../components/PaginationControls/PaginationControls";
import AppSnackbar from "../../components/AppSnackbar/AppSnackbar";
import PageLoader from "../../components/PageLoader/PageLoader";

function PartnerManagement() {
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const [partners, setPartners] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("approved");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* ================= HELPERS ================= */
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /* ================= FETCH PARTNERS ================= */
  const getBusinesses = useCallback(
    async (page, search) => {
      setLoading(true);
      try {
        const params = { page, search, status };

        const response = await apiRequest.get(
          endPoints.GET_PARTNER,
          params
        );

        if (response.success) {
          const { items, pagination } = response.data;
          setPartners(items);
          setCurrentPage(pagination.current_page);
          setTotalPages(pagination.total_pages);
        }
      } catch (error) {
        showSnackbar("Failed to fetch partners", "error");
      } finally {
        setLoading(false);
      }
    },
    [status]
  );

  /* ================= STATUS UPDATE ================= */
  const handlePartnerStatus = async (partnerId, status) => {
    try {
      await apiRequest.patch(`/api/partners/${partnerId}`, { status });

      setPartners((prev) =>
        prev.map((p) =>
          p._id === partnerId ? { ...p, status } : p
        )
      );

      showSnackbar(`Partner ${status} successfully`);
    } catch (error) {
      showSnackbar("Failed to update status", "error");
    }
  };

  /* ================= DETAILS ================= */
  const handleViewDetails = (partner) => {
    navigate(`/dashboard/partner/details/${partner.id}`);
  };

  /* ================= EFFECT ================= */
  useEffect(() => {
    getBusinesses(currentPage, searchValue);
  }, [currentPage, searchValue, getBusinesses]);

  /* ================= RENDER ================= */
  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Partners List
                </MDTypography>
              </MDBox>

              <MDBox pt={3} px={2}>
                {/* SEARCH */}
                <MDBox display="flex" justifyContent="flex-end" mb={2}>
                  <TextField
                    label="Search"
                    variant="outlined"
                    placeholder="Search partners"
                    value={searchValue}
                    onChange={(e) => {
                      setCurrentPage(1);
                      setSearchValue(e.target.value);
                    }}
                    sx={{ width: 300 }}
                  />
                </MDBox>

                {/* TABLE */}
                <PartnerTable
                  partners={partners}
                  onActionClick={handlePartnerStatus}
                  onViewDetails={handleViewDetails}
                />

                {/* PAGINATION */}
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />

      {/* LOADER */}
      <PageLoader open={loading} />

      {/* SNACKBAR */}
      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </DashboardLayout>
  );
}

export default PartnerManagement;
